/**
 * AcceptInvite.tsx — Public accept-invitation page (Phase 9 SC-1, D-09-04).
 *
 * Flow:
 *   1. URL: /accept-invite?token=XXX  (token from invite email)
 *   2. User sets display name + password + confirm
 *   3. POST /users/accept-invitation → on success navigate /login?invited=1
 *   4. If no token or invalid token: show error card (no form)
 *
 * Layout mirrors Login.tsx — centered card, ThemeSwitch top-right, role subtitle.
 * Conforms to 09-UI-SPEC Screen 3.
 */

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { operatorsApi, ApiRequestError } from '../lib/api-client.js';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  ThemeSwitch,
} from '@crm/ui';

export function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [displayName, setDisplayName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = React.useState(false);

  // Update page title
  React.useEffect(() => {
    document.title = 'MaloumDash — Accept Invitation';
  }, []);

  const acceptMut = useMutation({
    mutationFn: () =>
      operatorsApi.acceptInvitation({ token, password, displayName: displayName.trim() }),
    onSuccess: () => {
      navigate('/login?invited=1', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        const msg =
          typeof err.body.message === 'string'
            ? err.body.message
            : err.body.message.join('; ');
        // Treat 400/404 with "invalid" or "expired" as token errors
        if (
          err.status === 400 ||
          err.status === 404 ||
          msg.toLowerCase().includes('invalid') ||
          msg.toLowerCase().includes('expired')
        ) {
          setTokenInvalid(true);
        } else {
          setErrorMsg(msg);
        }
      } else {
        setErrorMsg((err as Error).message ?? 'Something went wrong');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!displayName.trim()) {
      setErrorMsg('Display name is required.');
      return;
    }
    if (displayName.trim().length > 80) {
      setErrorMsg('Display name must be 80 characters or fewer.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    acceptMut.mutate();
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeSwitch />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-bold text-text">MaloumDash</h1>
          <p className="text-sm text-text-muted mt-1">Admin console</p>
        </div>

        {tokenInvalid || !token ? (
          /* Invalid / missing token error card */
          <Card>
            <CardContent className="flex flex-col gap-4 pt-4">
              <p className="text-sm text-danger" role="alert">
                This invitation link is invalid or has expired.
              </p>
              <a href="/login" className="text-accent text-sm">
                Return to sign in
              </a>
            </CardContent>
          </Card>
        ) : (
          /* Accept invite form */
          <Card>
            <CardHeader>
              <CardTitle>Accept Invitation</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <p className="text-sm text-text-muted">
                  Set your display name and choose a password to activate your account.
                </p>
                <Input
                  label="Display name"
                  type="text"
                  maxLength={80}
                  autoComplete="name"
                  required
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setErrorMsg(null);
                  }}
                  disabled={acceptMut.isPending}
                />
                <Input
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrorMsg(null);
                  }}
                  disabled={acceptMut.isPending}
                />
                <Input
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrorMsg(null);
                  }}
                  disabled={acceptMut.isPending}
                />

                {errorMsg && (
                  <p className="text-sm text-danger" role="alert">
                    {errorMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  loading={acceptMut.isPending}
                  className="w-full justify-center"
                >
                  Accept Invitation
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
